import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { PresenceStrip } from "@/components/PresenceStrip";
import { useProperty } from "@/contexts/PropertyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BadgeCheck, ShieldAlert, ShieldX, ShieldQuestion, Mail, Pencil } from "lucide-react";

const STATUS_STYLE = {
  current: { icon: BadgeCheck, cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10", label: "Current" },
  expiring: { icon: ShieldAlert, cls: "border-amber-500/40 text-amber-300 bg-amber-500/10", label: "Expiring" },
  expired: { icon: ShieldX, cls: "border-rose-500/40 text-rose-300 bg-rose-500/10", label: "Expired" },
  missing: { icon: ShieldQuestion, cls: "border-zinc-500/40 text-zinc-300 bg-zinc-500/10", label: "Missing" },
};

function StatusPill({ status, days }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.missing;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={s.cls} data-testid={`status-${status}`}>
      <Icon className="h-3 w-3 mr-1" /> {s.label}
      {days !== null && days !== undefined && status !== "current" && status !== "missing" && (
        <span className="ml-1 font-mono text-[10px]">{days < 0 ? `${Math.abs(days)}d ago` : `${days}d`}</span>
      )}
    </Badge>
  );
}

function EditDialog({ vendor, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open) setForm({
      coi: { ...(vendor.coi || {}) }, w9: { ...(vendor.w9 || {}) },
      license: { ...(vendor.license || {}) }, notes: vendor.notes || "",
    });
  }, [open, vendor]);
  const save = async () => {
    try {
      await api.put(`/admin/vendor-compliance/${vendor.vendor_id}`, form);
      toast.success("Compliance updated"); setOpen(false); onSaved();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-white/10" data-testid={`edit-${vendor.vendor_id}`}><Pencil className="h-3 w-3 mr-1" /> Edit</Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-lg">
        <DialogHeader><DialogTitle>{vendor.vendor_name} — compliance</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded border border-white/10 p-3">
            <div className="overline text-[10px] mb-2">Certificate of Insurance</div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Carrier</Label><Input data-testid="coi-carrier" value={form.coi?.carrier || ""} onChange={(e) => setForm({ ...form, coi: { ...form.coi, carrier: e.target.value } })} className="mt-1 bg-white/[0.04] border-white/10 text-xs" /></div>
              <div><Label>Policy #</Label><Input value={form.coi?.policy_no || ""} onChange={(e) => setForm({ ...form, coi: { ...form.coi, policy_no: e.target.value } })} className="mt-1 bg-white/[0.04] border-white/10 text-xs" /></div>
              <div><Label>Coverage USD</Label><Input type="number" value={form.coi?.coverage_usd || ""} onChange={(e) => setForm({ ...form, coi: { ...form.coi, coverage_usd: parseFloat(e.target.value) || 0 } })} className="mt-1 bg-white/[0.04] border-white/10 text-xs" /></div>
              <div><Label>Expires</Label><Input data-testid="coi-expires" type="date" value={(form.coi?.expires_at || "").slice(0, 10)} onChange={(e) => setForm({ ...form, coi: { ...form.coi, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" } })} className="mt-1 bg-white/[0.04] border-white/10 text-xs" /></div>
            </div>
          </div>
          <div className="rounded border border-white/10 p-3">
            <div className="overline text-[10px] mb-2">W-9</div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>EIN last 4</Label><Input data-testid="w9-ein" maxLength={4} value={form.w9?.ein_last4 || ""} onChange={(e) => setForm({ ...form, w9: { ...form.w9, ein_last4: e.target.value } })} className="mt-1 bg-white/[0.04] border-white/10 text-xs" /></div>
              <div><Label>Signed on</Label><Input data-testid="w9-signed" type="date" value={(form.w9?.signed_at || "").slice(0, 10)} onChange={(e) => setForm({ ...form, w9: { ...form.w9, signed_at: e.target.value ? new Date(e.target.value).toISOString() : "" } })} className="mt-1 bg-white/[0.04] border-white/10 text-xs" /></div>
            </div>
          </div>
          <div className="rounded border border-white/10 p-3">
            <div className="overline text-[10px] mb-2">License</div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Number</Label><Input value={form.license?.number || ""} onChange={(e) => setForm({ ...form, license: { ...form.license, number: e.target.value } })} className="mt-1 bg-white/[0.04] border-white/10 text-xs" /></div>
              <div><Label>State</Label><Input value={form.license?.state || ""} onChange={(e) => setForm({ ...form, license: { ...form.license, state: e.target.value } })} className="mt-1 bg-white/[0.04] border-white/10 text-xs" /></div>
              <div><Label>Expires</Label><Input type="date" value={(form.license?.expires_at || "").slice(0, 10)} onChange={(e) => setForm({ ...form, license: { ...form.license, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" } })} className="mt-1 bg-white/[0.04] border-white/10 text-xs" /></div>
            </div>
          </div>
          <Button data-testid="save-compliance" onClick={save} className="w-full bg-orange-500 hover:bg-orange-600">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function VendorCompliancePage() {
  const { currentProperty } = useProperty();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState(null);
  const [nudging, setNudging] = useState(new Set());
  const load = useCallback(async () => {
    try { const q = filter ? `?status=${filter}` : ""; const { data } = await api.get(`/admin/vendor-compliance${q}`); setItems(data.items || []); setCounts(data.counts || {}); }
    catch { setItems([]); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const nudge = async (vid, name) => {
    if (!window.confirm(`Send compliance nudge to ${name}?`)) return;
    // R74 — optimistic: stamp `last_nudged_at = now` on the row + disable button
    const snapshot = items;
    const nowIso = new Date().toISOString();
    setItems((prev) => prev.map((v) => (v.vendor_id === vid ? { ...v, last_nudged_at: nowIso } : v)));
    setNudging((prev) => new Set(prev).add(vid));
    try {
      await api.post(`/admin/vendor-compliance/${vid}/nudge`);
      toast.success(`Nudge sent to ${name}`);
      load();
    } catch (e) {
      setItems(snapshot);
      toast.error(apiErrorMessage(e) || "Nudge failed — restored");
    } finally {
      setNudging((prev) => { const s = new Set(prev); s.delete(vid); return s; });
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Vendors" title="Vendor Compliance" subtitle="Track COI, W-9, and license expiry across every vendor. Nudge before renewal, block before hiring."
                  actions={<PresenceStrip propertyId={currentProperty?.id} testId="vendor-compliance-presence" />}
      />
      <div className="grid grid-cols-4 gap-3 mb-4">
        {["expired", "missing", "expiring", "current"].map((k) => {
          const s = STATUS_STYLE[k]; const Icon = s.icon;
          return (
            <Card key={k} className={`border-white/10 bg-card p-3 cursor-pointer ${filter === k ? "ring-1 ring-orange-500/40" : ""}`} onClick={() => setFilter(filter === k ? null : k)} data-testid={`filter-${k}`}>
              <div className="overline text-[10px] flex items-center gap-1"><Icon className="h-3 w-3" /> {s.label}</div>
              <div className="text-2xl font-semibold">{counts[k] || 0}</div>
            </Card>
          );
        })}
      </div>
      <Card className="border-white/10 bg-card p-4">
        <div className="space-y-2">
          {items.map((v) => (
            <div key={v.vendor_id} className="rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-center justify-between" data-testid={`vendor-${v.vendor_id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{v.vendor_name} <span className="text-xs text-zinc-500 ml-1">{v.vendor_category}</span></div>
                <div className="flex items-center gap-2 mt-1 text-[10px]">
                  <span className="text-zinc-500">COI:</span><StatusPill status={v.coi_status} days={v.coi_days_until} />
                  <span className="text-zinc-500 ml-2">W-9:</span><StatusPill status={v.w9_status} />
                  <span className="text-zinc-500 ml-2">License:</span><StatusPill status={v.license_status} days={v.license_days_until} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <EditDialog vendor={v} onSaved={load} />
                <Button size="sm" variant="outline" className="border-white/10"
                        onClick={() => nudge(v.vendor_id, v.vendor_name)}
                        disabled={nudging.has(v.vendor_id)}
                        data-testid={`nudge-${v.vendor_id}`}>
                  <Mail className="h-3 w-3 mr-1" />
                  {nudging.has(v.vendor_id) ? "Sending…" : "Nudge"}
                </Button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">No vendors match this filter.</div>}
        </div>
      </Card>
    </div>
  );
}
